import type { SnakeCase } from "type-fest";

/**
 * The closed sets a catalogue setting chooses from.
 *
 * A catalogue setting names something that has to exist — a bundle to render, an
 * instrument to score against — so the set lives in code beside the thing it
 * names rather than being free text an administrator can mistype into a blank
 * screen. Widening one is a line here; narrowing one is a migration, because
 * stored settings may still name what was removed.
 */

/**
 * A version of an assessment instrument a body may be entitled to.
 *
 * Each value is a published instrument's name without its last segment — see
 * `instrumentPrefixes`, which owns that operation and the reason for it.
 * Starting an assessment under one of these pins the newest published version
 * extending it.
 *
 * Written out rather than derived from the manifest, because a stored setting
 * names one of these and the compiler has to know the set — and checked against
 * the manifest by a test, so it cannot offer a version nothing publishes.
 */
export const OfferedInstrumentVersionValues = [
  "sample/core/1.0",
  "sample/core/1.1",
  "sample_cans/comprehensive/1.0",
  "sample/core/1.2",
  "sample_cans/mini/1.0",
] as const;
export type OfferedInstrumentVersion =
  (typeof OfferedInstrumentVersionValues)[number];

/**
 * A language the product is offered in.
 *
 * Which of these a body offers is policy — somebody commissions the translations
 * — and which one a person reads in is theirs, within that. The two are separate
 * settings for that reason.
 */
export const LanguageValues = ["en", "cy"] as const;
export type Language = (typeof LanguageValues)[number];

/**
 * What the product calls the subject of a record.
 *
 * A register rather than a language: `young_person` and `child` are both English,
 * and a body writes in one of them because of who its records are about. Composed
 * with the language rather than folded into it: folding register into language
 * multiplies the bundles by every language added.
 */
export const VocabularyPackValues = ["young_person", "child"] as const;
VocabularyPackValues satisfies readonly SnakeCase<
  (typeof VocabularyPackValues)[number]
>[];
export type VocabularyPack = (typeof VocabularyPackValues)[number];
